import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Plus, X } from "lucide-react";
import { api, type MessagingChannel } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/Input";

type Props = {
  channel: MessagingChannel;
  selected: string[];
  onChange: (targets: string[]) => void;
};

/**
 * Selettore destinatari per i canali di GRUPPO/broadcast:
 * - whatsapp_group     → gruppi WhatsApp (jid @g.us) da moving_groups.yaml
 * - playtomic_broadcast → community Playtomic (chat g:) + chat custom (t:/g:)
 */
export function MessagingTargetPicker({ channel, selected, onChange }: Props) {
  const [custom, setCustom] = useState("");
  const q = useQuery({
    queryKey: ["messaging-groups"],
    queryFn: () => api.messagingGroups(),
    staleTime: 60_000,
  });

  const toggle = (id: string) => {
    onChange(
      selected.includes(id)
        ? selected.filter((x) => x !== id)
        : [...selected, id],
    );
  };

  const addCustom = () => {
    const v = custom.trim();
    if (v && !selected.includes(v)) onChange([...selected, v]);
    setCustom("");
  };

  if (q.isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Carico gruppi…
      </div>
    );
  }

  const waGroups = q.data?.whatsapp_groups ?? [];
  const communities = q.data?.playtomic_communities ?? [];

  const list =
    channel === "whatsapp_group"
      ? waGroups.map((g) => ({ id: g.jid, label: g.name, sub: g.type }))
      : communities.map((c) => ({ id: c.chat_id, label: c.name, sub: "community" }));

  return (
    <div className="space-y-3">
      <div className="text-xs font-semibold uppercase text-slate-500">
        {channel === "whatsapp_group"
          ? "Gruppi WhatsApp destinatari"
          : "Community / chat Playtomic destinatarie"}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {list.map((g) => {
          const active = selected.includes(g.id);
          return (
            <button
              key={g.id}
              type="button"
              onClick={() => toggle(g.id)}
              title={g.id}
              className={cn(
                "rounded-full border px-2.5 py-1 text-xs font-medium transition",
                active
                  ? "border-moving-500 bg-moving-500 text-white"
                  : "border-slate-200 bg-white text-slate-600 hover:border-slate-300",
              )}
            >
              {g.label}
            </button>
          );
        })}
        {list.length === 0 ? (
          <span className="text-xs text-slate-400">Nessun gruppo disponibile</span>
        ) : null}
      </div>

      {/* Chat custom (es. chat torneo t:UUID) */}
      <div className="flex items-center gap-2">
        <Input
          value={custom}
          onChange={(e) => setCustom(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addCustom();
            }
          }}
          placeholder={
            channel === "whatsapp_group"
              ? "Aggiungi jid manuale (…@g.us)"
              : "Aggiungi chat_id (g:… o t:…)"
          }
          className="flex-1 text-xs"
        />
        <button
          type="button"
          onClick={addCustom}
          className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1.5 text-xs text-slate-600 hover:bg-slate-50"
        >
          <Plus className="h-3.5 w-3.5" /> Aggiungi
        </button>
      </div>

      {/* Selezionati con possibilità di rimuovere (per i custom) */}
      {selected.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {selected.map((id) => (
            <span
              key={id}
              className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-700"
            >
              {id}
              <button type="button" onClick={() => toggle(id)} aria-label="rimuovi">
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}
